import classNames from 'classnames';
import React, { ReactNode, useEffect, useRef } from 'react';
import styles from './genericpopup.module.scss';

export type GenericPopupProps = {
  isOpen: boolean;
  children?: ReactNode;
  className?: string;
  isModal?: boolean;
  clickOutsideCallback: () => void;
};

export function GenericPopup({
  isOpen,
  children,
  className,
  isModal,
  clickOutsideCallback,
}: GenericPopupProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const popupWrapRef = useRef<HTMLDivElement>(null);

  const useOutsideClick = (callback: () => void) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (
          isOpen &&
          ref.current &&
          !ref.current.contains(event.target as Node) &&
          popupWrapRef.current === event.target
        ) {
          callback();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [callback]);

    return ref;
  };

  const popupRef = useOutsideClick(() => {
    clickOutsideCallback();
  });

  return (
    <div
      className={classNames(
        styles.popup,
        {
          [styles.isOpen]: open,
          [styles.hidden]: !isOpen,
          [styles.modal]: isModal,
        },
        className
      )}
      ref={popupWrapRef}
    >
      <div className={classNames(styles.popupBody)} ref={popupRef}>
        {children}
      </div>
    </div>
  );
}
